/**
 * Retrieves the translation of text.
 */
import { __ } from "@wordpress/i18n";
import { useBlockProps, InspectorControls } from "@wordpress/block-editor";
import {
  PanelBody,
  PanelRow,
  RangeControl,
  Spinner,
  CheckboxControl,
  SelectControl,
} from "@wordpress/components";
import { useEffect, useState, useCallback } from "@wordpress/element";

export default function Edit({ attributes, setAttributes }) {
  const blockProps = useBlockProps();
  const {
    postCount = 5,
    postsToSkip = 0,
    activateLoadMore = false,
    loopTemplate = "template-1",
  } = attributes;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    setError(null);

    wp.apiFetch({
      path: `/wp/v2/posts?per_page=${postCount}&offset=${postsToSkip}&_embed=true`,
    })
      .then((posts) => {
        setPosts(posts);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching posts:", error);
        setError(__("Failed to fetch posts.", "posts-loop"));
        setLoading(false);
      });
  }, [postCount, postsToSkip]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return (
    <div {...blockProps}>
      <InspectorControls>
        <PanelBody title={__("Settings", "posts-loop")}>
          <PanelRow>
            <RangeControl
              label={__("Number of Posts", "posts-loop")}
              value={postCount}
              onChange={(value) => setAttributes({ postCount: value })}
              min={1}
              max={20}
            />
          </PanelRow>
          <PanelRow>
            <RangeControl
              label={__("Posts to Skip", "posts-loop")}
              value={postsToSkip}
              onChange={(value) => setAttributes({ postsToSkip: value })}
              min={0}
              max={20}
            />
          </PanelRow>
          <PanelRow>
            <CheckboxControl
              label={__("Activate Load More", "posts-loop")}
              checked={activateLoadMore}
              onChange={(value) => setAttributes({ activateLoadMore: value })}
            />
          </PanelRow>
          <PanelRow>
            <SelectControl
              label={__("Loop Template", "posts-loop")}
              value={loopTemplate}
              options={[
                {
                  label: __("Template 1: row", "posts-loop"),
                  value: "template-1",
                },
                {
                  label: __(
                    "Template 2: intro, flex and columns",
                    "posts-loop"
                  ),
                  value: "template-2",
                },
              ]}
              onChange={(value) => setAttributes({ loopTemplate: value })}
              defaultValue="template-1"
            />
          </PanelRow>
        </PanelBody>
      </InspectorControls>

      <div className="my-post-loop-block">
        {activateLoadMore && <button onClick={fetchPosts}>Load More</button>}
        {loading ? (
          <Spinner />
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <ul className="list-unstyled">
            {posts.map((post) => (
              <li key={post.id} className={`d-flex ${loopTemplate}`}>
                {post._embedded?.["wp:featuredmedia"]?.[0]?.source_url ? (
                  <img
                    src={post._embedded["wp:featuredmedia"][0].source_url}
                    alt={post.title.rendered}
                    style={{ width: "235px", height: "161px" }}
                  />
                ) : (
                  <div className="placeholder-image">No Image</div>
                )}
                <div className="d-flex flex-column">
                  <h3>{post.title.rendered}</h3>
                  <p
                    dangerouslySetInnerHTML={{
                      __html:
                        post.excerpt.rendered
                          .split(" ")
                          .slice(0, 25)
                          .join(" ") + "...",
                    }}
                  ></p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
